import qs from 'qs';
import moment from 'moment';

const INVALID_NUMBER_LABEL = '--';

/**
 * value parse to number in these cases:
 * Number(undefined) = NaN
 * Number(null) = 0
 * Number(Symbol) = NaN
 * Number({}) = NaN
 * Number({a: 1}) = NaN
 * Number({1: 2}) = NaN
 * Number([]) = 0
 * Number([2]) = 2
 * Number([2,1]) = NaN
 * Number(['a']) = NaN
 * Number(() => {}) = NaN
 * Number(true) = 1
 * Number(false) = 0
 * Number("abc") = NaN
 * Number("") = 0
 * Number("0") = 0
 * Number("123.3") = 123.3
 * Number("-123") = -123
 * Number(123) = 123
 * Number(-123) = -123
 * Number(0) = 0
 * Number(NaN) = NaN
 * Number(Infinity) = Infinity
 *
 * value typeof in these cases:
 * typeof undefined = 'undefined'
 * typeof null = 'object'
 * typeof Symbol = 'function'
 * typeof {} = 'object'
 * typeof [] = 'object'
 * typeof (() => {}) = 'function'
 * typeof true = 'boolean'
 * typeof "" = 'string'
 * typeof "abc" = 'string'
 * typeof "123" = 'string'
 * typeof 2 = 'number'
 * typeof -123.4 = 'number'
 * typeof NaN = 'number'
 * typeof Infinity = 'number'
 */
const isValidNumber = (target: any) => {
    const type = typeof target;
    if (!(type === 'number' || type === 'string')) return false;
    if (target === '') return false;
    return Number.isFinite(Number(target));
};

const isPositiveInteger = (target: any) => {
    if (isValidNumber(target)) {
        const value = Number(target);
        return Number.isSafeInteger(value) && value > 0;
    }
    return false;
};

const isNaturalNumber = (target: any) => {
    if (isValidNumber(target)) {
        const value = Number(target);
        return Number.isSafeInteger(value) && value >= 0;
    }
    return false;
};

const appendQuery = (param: object) => {
    return qs.stringify(param, {addQueryPrefix: true, arrayFormat: 'repeat'});
};

const formatPrice = (price?: number) => {
    if (isValidNumber(price)) {
        return Number(price).toFixed(2);
    }
    return INVALID_NUMBER_LABEL;
};

const formatDate = (ms?: number) => {
    if (isValidNumber(ms)) {
        return moment(Number(ms)).format('YYYY-MM-DD');
    }
    return INVALID_NUMBER_LABEL;
};

const formatPercentage = (percent?: number, friction = 2) => {
    if (isValidNumber(percent)) {
        return `${Number(percent).toFixed(friction)}%`;
    }
    return INVALID_NUMBER_LABEL;
};

const isArrayHasData = (array: any) => {
    return Array.isArray(array) && array.length > 0;
};

const HEX = '0123456789abcdef'.split('');

const toHex = (a: number[]) => {
    return a.map((i) => HEX[(i >> 4) & 0x0f] + HEX[i & 0x0f]).join('');
};

const fromHex = (str: string) => {
    const buffer = new Uint8Array(str.length / 2);
    for (let i = 0; i < buffer.length; i++) {
        buffer[i] = parseInt(str.substring(i * 2, i * 2 + 2), 16);
    }
    return buffer;
};

export default {
    isValidNumber,
    isNaturalNumber,
    isPositiveInteger,
    appendQuery,
    formatPrice,
    formatDate,
    formatPercentage,
    isArrayHasData,
    toHex,
    fromHex,
};
